import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Input, Select } from 'antd';
import { useChangeAbsenceResponsibleMutation } from 'app/services/edpApiV1/absence';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import { useAppDispatch, useAppSelector } from 'app/store';
import { uiModel } from 'entities/ui';
import { viewerModel } from 'entities/viewer';
import { getErrorData } from 'shared/helpers/utils';
import { usePath } from 'shared/hooks';
import { useNotifications } from 'shared/hooks/use-notifications';
import { TPreparedSingleAbsenceData } from 'shared/types/absence.types';
import { RolesUser } from 'shared/enums';
import style from './index.module.scss';

const AbsenceUserForm = ({ dataAbsence }: { dataAbsence: TPreparedSingleAbsenceData[] }) => {
  const { id } = useParams();
  const path = usePath(2);
  const [form] = Form.useForm();
  const [btnVisible, setBtnVisible] = useState<boolean>(false);
  const [selectDisabled, setSelectDisabled] = useState<boolean>(false);

  const absenceHrId = dataAbsence[0]?.hr?.id;

  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });

  const { role, id: profileId } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const dispatch = useAppDispatch();

  const [changeResponsibleTrigger, resultChangeResponsible] = useChangeAbsenceResponsibleMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  const {
    isSuccess: isSuccessChangeResponsible,
    isError: isErrorChangeResponsible,
    error: errorChangeResponsible,
    reset: resetChangeResponsible,
  } = resultChangeResponsible;

  const onFinish = async (values: { hrId: number }) => {
    const { hrId } = values;

    const bodyRequest = {
      requestId: Number(id),
      hrId,
    };
    if (hrId !== profileId) {
      dispatch(uiModel.Common.actions.blockHrActions(true));
    } else {
      dispatch(uiModel.Common.actions.blockHrActions(false));
    }
    await changeResponsibleTrigger(bodyRequest);
    setBtnVisible(false);
  };

  const selectHandler = (selectedHrId: number) => {
    if (selectedHrId === absenceHrId) {
      setBtnVisible(false);
    } else {
      setBtnVisible(true);
    }
  };

  useEffect(() => {
    if (path === 'myArchive' && role !== RolesUser.HRDirector && role !== RolesUser.SecondLine) {
      setSelectDisabled(true);
    }
    if (path === 'archive') {
      setSelectDisabled(true);
    }
  }, [role, path]);

  useEffect(() => {
    if (role !== RolesUser.HRDirector && role !== RolesUser.SecondLine) {
      setSelectDisabled(true);
    }
  }, [role]);

  useEffect(() => {
    if (profileId && absenceHrId) {
      if (profileId !== absenceHrId) {
        dispatch(uiModel.Common.actions.blockHrActions(true));
      }
    }
    return () => {
      dispatch(uiModel.Common.actions.blockHrActions(false));
    };
  }, [profileId, absenceHrId]);

  useEffect(() => {
    if (dataAbsence[0]) {
      if (
        dataAbsence[0]?.status?.name === 'Готово' ||
        dataAbsence[0]?.status?.name === 'Отклонено' ||
        dataAbsence[0]?.zupRequestStatus !== null
      ) {
        setSelectDisabled(true);
      }
    }
  }, [dataAbsence]);

  useEffect(() => {
    const author = dataAbsence[0]?.creator;
    const authorFullName = `${author?.lastName} ${author?.name} ${author?.middleName ?? ''}`;

    if (dataAbsence && dataAbsence?.length > 0) {
      form.setFields([
        {
          name: 'tabel',
          value: dataAbsence[0]?.employee?.personnelNumber,
        },
        {
          name: 'division',
          value: dataAbsence[0]?.employee?.division,
        },
        {
          name: 'position',
          value: dataAbsence[0]?.employee?.position,
        },
        {
          name: 'hrId',
          value: dataAbsence[0]?.hr?.id,
        },
        {
          name: 'supervisor',
          value: authorFullName,
        },
      ]);
    }
  }, [dataAbsence, form]);

  useNotifications(
    isSuccessChangeResponsible,
    isErrorChangeResponsible,
    'Ответственный назначен',
    errorChangeResponsible?.message,
    resetChangeResponsible,
  );

  return (
    <Form form={form} onFinish={onFinish} layout="vertical" name="userInfo" className={style.userInfo}>
      <div className={style.groupItem}>
        <Form.Item name="tabel" label="Табельный номер" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>
        <Form.Item name="division" label="Подразделение" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>
      </div>

      <div className={style.groupItem}>
        <Form.Item name="position" label="Должность" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>

        {allSubordinates && (
          <>
            <Form.Item
              name="hrId"
              label="Ответственный HR"
              labelAlign="left"
              className={style.inputWrap}
              rules={[
                {
                  validator: async (_, v) => {
                    if (v === absenceHrId) return Promise.reject();
                  },
                  message: 'Выберите нового ответственного',
                },
              ]}
            >
              <Select placeholder="Выберите из списка" onSelect={selectHandler} disabled={selectDisabled}>
                {allSubordinates.map(({ id, name, middleName, lastName }) => (
                  <Select.Option key={id} value={id}>
                    {`${lastName} ${name} ${middleName ?? ''}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}
      </div>

      <div className={style.groupItem}>
        <Form.Item name="supervisor" label="Автор неявки" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>
      </div>

      {btnVisible && (
        <Form.Item labelAlign="left" className={style.btn}>
          <Button htmlType="submit" type="primary">
            Сохранить
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default AbsenceUserForm;
